import { USERS, PROPERTIES } from '../../../firebase';
import { mortgageFormSectionCalculator } from '../../utility/mortgageCalculator';
import { getNumberFromString } from '../../utility/formatNumbers';
import getSum from '../../utility/getSum';
import convertUnitMoney from '../../utility/convertUnitMoney';
import { getTableSum } from '../../utility/getTableSum';
import getTotalRentIncome from '../../utility/getTotalRentIncome';
import { isValue } from '../../utility/isValue';
import FormUIOptions from '../../components/formBuilder/formElements/formUIOptions';
import CMHCInsurancePremiums from '../../constants/cmhcPremiums';
import criteriaDefaults from '../../constants/investmentCriteria';
import getNumberWithCommas from '../../../../functions/createReport/getNumberWithCommas';
import calculations from './calculations';
import longTermDefaults from '../../constants/longTermDefaults';
import simpleCalculation from './simple-calculation';

// const lenderLists = [
//   {
//     label: 'First National',
//     layout: 'default',
//     value: 'first-national',
//   },
//   {
//     label: 'Scotiabank',
//     layout: 'default',
//     value: 'scotiabank',
//   },
//   {
//     label: 'Home Trust',
//     layout: 'default',
//     value: 'home-trust',
//   },
//   {
//     label: 'MCAP',
//     layout: 'default',
//     value: 'mcap',
//   },
//   {
//     label: 'TD Canada Trust',
//     layout: 'default',
//     value: 'td-canada-trust',
//   },
//   {
//     label: 'Equitable Bank',
//     layout: 'default',
//     value: 'equitable-bank',
//   },
//   {
//     label: 'Other',
//     layout: 'default',
//     value: 'other',
//   },
// ];

const rentDueDateOptions = [
  {
    label: '1st of the month',
    layout: 'default',
    default: true,
    value: '1st',
  },
  {
    label: '15th of the month',
    layout: 'default',
    value: '15th',
  },
  {
    label: 'Other',
    layout: 'default',
    value: 'other',
  },
];

const numberOptions = [
  { label: '0', layout: 'default', value: '0' },
  {
    label: '1',
    layout: 'default',
    value: '1',
  },
  { label: '2', layout: 'default', value: '2' },
  { label: '3', layout: 'default', value: '3' },
  { label: '4', layout: 'default', value: '4' },
  { label: '5', layout: 'default', value: '5' },
  { label: '6', layout: 'default', value: '6' },
  { label: '7', layout: 'default', value: '7' },
  { label: '8', layout: 'default', value: '8' },
  { label: '9', layout: 'default', value: '9' },
  { label: '10', layout: 'default', value: '10' },
  { label: '11', layout: 'default', value: '11' },
  { label: '12', layout: 'default', value: '12' },
  { label: '13', layout: 'default', value: '13' },
  { label: '14', layout: 'default', value: '14' },
  { label: '15+', layout: 'default', value: '15' },
];

const purchaseDefaults = {
  downPayment: {
    derivedFrom: 'purchasePrice',
    unit: '%',
    value: 20,
  },
  interestRate: 6,
  amortization: 30,
  buildingInspection: 600,
  appraisal: 750,
  legalFees: 2500,
};

const embeddedForm = window.location.href.includes('embedded');

const enterTotalDefault = embeddedForm;

export default {
  messageOnFinish: 'Proceed to the next step to explore BRRRR Scenario',
  pathToSource: [
    {
      type: 'collection',
      value: USERS,
    },
    {
      type: 'user',
      value: 'uid',
    },
    {
      type: 'collection',
      value: PROPERTIES,
    },
    {
      type: 'param',
      value: 'propertyId',
    },
  ],
  inputs: {
    title: 'Purchase Worksheet',
    description:
      'Add financial details about your property and mortgage to calculate your cash required to close and monthly cash flow',
    submitButtons: 'purchaseWorksheet',
    submitButtonLabel: 'Submit & Analyze',
    submitButtonDescription:
      "Submit your information and we'll analyze the details.",
    sideBarSchema: embeddedForm ? false : true,
    schema: [
      {
        sectionLabel: 'Purchase Details',
        sectionDescription: 'Add basic purchase details.',
        sectionValue: 'purchaseDetails',
        schema: [
          {
            type: FormUIOptions.Money,
            label: 'Asking Price',
            layout: 'default',
            excludeFromEmbeddedForm: true,
            name: 'askingPrice',
            unit: '$',
          },
          {
            type: FormUIOptions.Text,
            label: 'Property Name',
            layout: 'default',
            description: 'Provide an optional property name',
            name: 'propertyName',
            onlyInEmbeddedForm: true,
          },
          {
            type: FormUIOptions.Money,
            label: 'Purchase Price',
            layout: 'default',
            name: 'purchasePrice',
            unit: '$',
            required: true,
          },
          {
            type: FormUIOptions.YesNo,
            label: 'Cash Purchase?',
            layout: 'default',
            name: 'cashPurchase',
            defaultValue: false,
          },
        ],
      },
      {
        sectionLabel: 'Financing Details',
        sectionDescription:
          'Add information about your mortgage(s). You can modify the default values below.',
        sectionValue: 'financingDetails',
        sectionCondition: {
          field: 'cashPurchase',
          isLocal: true,
          value: false,
        },
        // sectionDescription: 'Information about your mortgage(s)',
        schema: [
          {
            type: FormUIOptions.Header,
            label: 'Mortgage Details',
          },
          {
            type: FormUIOptions.UnitMoney,
            label: 'Down Payment',
            layout: 'default',
            name: 'downPayment',
            derivedFrom: 'purchasePrice',
            selectOptions: FormUIOptions.selectOptions.percentage,
            defaultValue: purchaseDefaults.downPayment,
            required: true,
            condition: {
              field: 'cashPurchase',
              isLocal: true,
              value: false,
            },
          },
          {
            type: FormUIOptions.Number,
            label: 'Interest Rate',
            layout: 'default',
            name: 'interestRate',
            defaultValue: purchaseDefaults.interestRate,
            unit: '%',
            required: true,
            condition: {
              field: 'cashPurchase',
              isLocal: true,
              value: false,
            },
          },
          {
            type: FormUIOptions.YesNo,
            label: 'Interest-only?',
            layout: 'default',
            name: 'mortgageInterestOnly',
            defaultValue: false,
            required: true,
            condition: {
              field: 'cashPurchase',
              isLocal: true,
              value: false,
            },
          },
          {
            type: FormUIOptions.Number,
            label: 'Loan Period',
            layout: 'default',
            name: 'interestOnlyLoanPeriod',
            unit: 'years',
            condition: [
              {
                field: 'mortgageInterestOnly',
                isLocal: true,
                value: true,
              },
              {
                field: 'cashPurchase',
                isLocal: true,
                value: false,
              },
            ],
          },
          {
            type: FormUIOptions.YesNo,
            label: 'Interest paid monthly?',
            layout: 'default',
            name: 'interestOnlyMonthly',
            required: true,
            description:
              'Is the interest on your loan paid monthly or at the end of the loan period?',
            defaultValue: false,
            condition: [
              {
                field: 'mortgageInterestOnly',
                isLocal: true,
                value: true,
              },
              {
                field: 'cashPurchase',
                isLocal: true,
                value: false,
              },
            ],
          },
          {
            type: FormUIOptions.Number,
            label: 'Amortization',
            layout: 'default',
            name: 'amortization',
            defaultValue: purchaseDefaults.amortization,
            description: 'Mortgage period',
            unit: 'years',
            required: true,
            condition: [
              {
                field: 'mortgageInterestOnly',
                isLocal: true,
                value: !true,
              },
              {
                field: 'cashPurchase',
                isLocal: true,
                value: false,
              },
            ],
          },
          // {
          //   type: FormUIOptions.Select,
          //   label: 'Who is your lender?',
          //   layout: 'default',
          //   name: 'lender',
          //   options: lenderLists,
          //   excludeFromEmbeddedForm: true,
          //   condition: {
          //     field: 'cashPurchase',
          //     isLocal: true,
          //     value: false,
          //   },
          // },
          // {
          //   type: FormUIOptions.Text,
          //   label: 'Other Lender',
          //   layout: 'default',
          //   name: 'lenderOther',
          //   condition: [
          //     {
          //       field: 'lender',
          //       isLocal: true,
          //       value: 'other',
          //     },
          //     {
          //       field: 'cashPurchase',
          //       isLocal: true,
          //       value: false,
          //     },
          //   ],
          // },
          {
            type: FormUIOptions.YesNo,
            label: 'Include CMHC Premium?',
            description:
              'Canada Mortgage and Housing Corporation insurance, for properties in Canada, assuming the property is classified as a standard rental. If you are using the CMHC MLI select plan or another property type, you will need to enter the insurance premium amount manually.',
            name: 'shouldCalculateCMHC',
            layout: 'default',
            defaultValue: false,
            excludeFromEmbeddedForm: true,
            condition: [
              {
                field: 'cashPurchase',
                isLocal: true,
                value: false,
              },

              {
                field: 'mortgageInterestOnly',
                isLocal: true,
                value: false,
              },
              {
                field: 'propertyInCanada',
                isLocal: true,
                value: [undefined, null, 1, ''],
              },
            ],
          },
          {
            type: FormUIOptions.YesNo,
            label: 'Calculate for me?',
            description:
              'If you select no, you will need to enter the insurance premium amount manually',
            name: 'shouldCalculateCMHCForMe',
            layout: 'default',
            defaultValue: true,
            condition: [
              {
                field: 'shouldCalculateCMHC',
                isLocal: true,
                value: true,
              },
              {
                field: 'cashPurchase',
                isLocal: true,
                value: false,
              },
              {
                field: 'mortgageInterestOnly',
                isLocal: true,
                value: false,
              },
              {
                field: 'propertyInCanada',
                isLocal: true,
                value: [undefined, null, 1, ''],
              },
            ],
          },
          {
            type: FormUIOptions.Money,
            label: 'CMHC/Insurance Premium',
            layout: 'default',
            name: 'cmhcPremiumManual',
            unit: '$',
            condition: [
              {
                field: 'shouldCalculateCMHC',
                isLocal: true,
                value: true,
              },
              {
                field: 'shouldCalculateCMHCForMe',
                isLocal: true,
                value: false,
              },
              {
                field: 'cashPurchase',
                isLocal: true,
                value: false,
              },
              {
                field: 'mortgageInterestOnly',
                isLocal: true,
                value: false,
              },
              {
                field: 'propertyInCanada',
                isLocal: true,
                value: [undefined, null, 1, ''],
              },
            ],
          },
          {
            type: FormUIOptions.Calculated,
            label: 'CMHC Premium',
            layout: 'default',
            name: 'cmhcPremium',
            unit: '$',
            derivedFrom: [
              'purchasePrice',
              'downPaymentAmount',
              'shouldCalculateCMHC',
              'shouldCalculateCMHCForMe',
              'cmhcPremiumManual',
              'mortgageInterestOnly',
              'cashPurchase',
              'propertyInCanada',
            ],
            calcFunction: (
              purchasePrice,
              downPaymentAmount,
              shouldCalculateCMHC,
              shouldCalculateCMHCForMe,
              cmhcPremiumManual,
              mortgageInterestOnly,
              cashPurchase,
              propertyInCanada
            ) => {
              if (!shouldCalculateCMHCForMe) {
                return cmhcPremiumManual;
              }
              if (
                !shouldCalculateCMHC ||
                mortgageInterestOnly ||
                cashPurchase ||
                propertyInCanada === 0
              ) {
                return null;
              }
              if (!isValue(purchasePrice, downPaymentAmount)) {
                return null;
              }

              const premiums = CMHCInsurancePremiums.rental;

              const mortgageAmount = purchasePrice - downPaymentAmount;
              const ltv = mortgageAmount / purchasePrice;
              const ltvRounded = Math.round(ltv * 100) / 100;

              switch (true) {
                case ltvRounded <= 0.65:
                  return mortgageAmount * premiums[0.65];
                case ltvRounded <= 0.7:
                  return mortgageAmount * premiums[0.7];
                case ltvRounded <= 0.75:
                  return mortgageAmount * premiums[0.75];
                case ltvRounded <= 0.8:
                  return mortgageAmount * premiums[0.8];
                default:
                  return mortgageAmount * premiums[0.85];
              }
            },
            condition: [
              {
                field: 'cashPurchase',
                isLocal: true,
                value: false,
              },
              {
                field: 'shouldCalculateCMHC',
                isLocal: true,
                value: true,
              },
              {
                field: 'mortgageInterestOnly',
                isLocal: true,
                value: false,
              },
              {
                field: 'propertyInCanada',
                isLocal: true,
                value: [undefined, null, 1, ''],
              },
            ],
          },
          {
            type: FormUIOptions.TableModal,
            layout: 'default',
            label: 'Additional Loans',
            itemName: 'Loan',
            groupStart: true,
            name: 'purchaseAdditionalFinancing',
            modalDescription: 'Add information about your loan',
            excludeFromEmbeddedForm: true,
            condition: {
              field: 'cashPurchase',
              isLocal: true,
              value: [false, undefined, ''],
            },
            tableSchema: [
              {
                type: FormUIOptions.Text,
                label: 'Loan Name',
                layout: 'default',
                name: 'loanNameTable',
                before: true,
              },
              {
                type: FormUIOptions.Money,
                label: 'Financing Amount',
                layout: 'default',
                name: 'mortgageAmountTable',
                isTableModalDisabled: true,
                required: true,
                unit: '$',
                before: true,
              },
              {
                type: FormUIOptions.Number,
                label: 'Interest Rate',
                layout: 'default',
                name: 'interestRateTable',
                unit: '%',
                required: true,
                before: true,
              },
              {
                type: FormUIOptions.YesNo,
                label: 'Interest-only?',
                layout: 'default',
                name: 'mortgageInterestOnlyTable',
                defaultValue: false,
                before: true,
              },
              {
                type: FormUIOptions.Number,
                label: 'Loan Period',
                layout: 'default',
                name: 'interestOnlyLoanPeriodTable',
                unit: 'years',
                condition: [
                  {
                    field: 'mortgageInterestOnlyTable',
                    isLocal: true,
                    value: true,
                  },
                ],
                before: true,
              },
              {
                type: FormUIOptions.YesNo,
                label: 'Interest paid monthly?',
                layout: 'default',
                name: 'interestOnlyMonthlyTable',
                description:
                  'Is the interest on your loan paid monthly or at the end of the loan period?',
                defaultValue: false,
                condition: [
                  {
                    field: 'mortgageInterestOnlyTable',
                    isLocal: true,
                    value: true,
                  },
                ],
                before: true,
              },
              {
                type: FormUIOptions.Number,
                label: 'Amortization',
                layout: 'default',
                name: 'amortizationTable',
                description: 'Mortgage period',
                unit: 'years',
                condition: [
                  {
                    field: 'mortgageInterestOnlyTable',
                    isLocal: true,
                    value: false,
                  },
                ],
                before: true,
              },
              {
                type: FormUIOptions.Calculated,
                label: 'Monthly Payment',
                layout: 'default',
                name: 'monthlyPaymentTable',
                unit: '$',
                derivedFrom: [
                  'mortgageAmountTable',
                  'interestRateTable',
                  'mortgageInterestOnlyTable',
                  'interestOnlyMonthlyTable',
                  'amortizationTable',
                ],
                calcFunction: (
                  mortgageAmountTable,
                  interestRateTable,
                  mortgageInterestOnlyTable,
                  interestOnlyMonthlyTable,
                  amortizationTable
                ) => {
                  return mortgageFormSectionCalculator(
                    mortgageAmountTable,
                    interestRateTable,
                    mortgageInterestOnlyTable,
                    interestOnlyMonthlyTable,
                    amortizationTable
                  );
                },
                before: true,
              },
            ],
          },
          {
            type: FormUIOptions.CalculatedSection,
            groupStart: true,
            name: 'mortgageCalculations',
            schema: [
              {
                type: FormUIOptions.Calculated,
                label: 'Down Payment',

                layout: 'default',
                name: 'downPaymentAmount',
                unit: '$',
                //in this case 'downPayment' is the original value calculated based on the first mortgage and downpayment %, and downPaymentAmount is the value after considering the second mortgage.
                derivedFrom: [
                  'downPayment',
                  'purchaseAdditionalFinancing',
                  'cashPurchase',
                  'purchasePrice',
                ],
                calcFunction: (
                  downPayment,
                  purchaseAdditionalFinancing,
                  cashPurchase,
                  purchasePrice
                ) => {
                  if (cashPurchase) {
                    return purchasePrice;
                  }

                  const initDownPayment = convertUnitMoney(downPayment);

                  const otherFinancingAmount = Array.isArray(
                    purchaseAdditionalFinancing
                  )
                    ? purchaseAdditionalFinancing.reduce((acc, curr) => {
                        return curr.mortgageAmountTable
                          ? acc + convertUnitMoney(curr.mortgageAmountTable)
                          : acc;
                      }, 0)
                    : 0;

                  return initDownPayment - otherFinancingAmount;
                },
                condition: {
                  field: 'cashPurchase',
                  isLocal: true,
                  value: false,
                },
              },
              {
                type: FormUIOptions.Calculated,
                label: 'Total Mortgage Amount',
                layout: 'default',
                name: 'totalMortgageAmount',
                unit: '$',
                condition: {
                  field: 'cashPurchase',
                  isLocal: true,
                  value: false,
                },
                derivedFrom: [
                  'purchasePrice',
                  'downPayment',
                  'cashPurchase',
                  'shouldCalculateCMHC',
                  'cmhcPremium',
                  'purchaseAdditionalFinancing',
                  'propertyInCanada',
                ],
                calcFunction: (
                  purchasePrice,
                  downPayment,
                  cashPurchase,
                  shouldCalculateCMHC,
                  cmhcPremium,
                  purchaseAdditionalFinancing,
                  propertyInCanada
                ) => {
                  if (cashPurchase) {
                    return 0;
                  }

                  const firstMortgage = purchasePrice - downPayment;

                  const otherFinancingAmount = Array.isArray(
                    purchaseAdditionalFinancing
                  )
                    ? purchaseAdditionalFinancing.reduce((acc, curr) => {
                        return curr.mortgageAmountTable
                          ? acc + convertUnitMoney(curr.mortgageAmountTable)
                          : acc;
                      }, 0)
                    : 0;

                  let totalMortgage = firstMortgage + otherFinancingAmount;

                  if (
                    shouldCalculateCMHC &&
                    cmhcPremium &&
                    propertyInCanada !== 0
                  ) {
                    return totalMortgage + cmhcPremium;
                  }
                  return totalMortgage;
                },
              },
              {
                type: FormUIOptions.Calculated,
                label: 'Total Monthly Payment',
                layout: 'default',
                name: 'totalMonthlyMortgagePayment',
                unit: '$',
                condition: {
                  field: 'cashPurchase',
                  isLocal: true,
                  value: false,
                },
                derivedFrom: [
                  'interestRate',
                  'purchasePrice',
                  'downPayment',
                  'amortization',
                  'mortgageInterestOnly',
                  'interestOnlyMonthly',
                  'cashPurchase',
                  'shouldCalculateCMHC',
                  'cmhcPremium',
                  'purchaseAdditionalFinancing',
                  'propertyInCanada',
                ],
                calcFunction: (
                  interestRate,
                  purchasePrice,
                  downPayment,
                  amortization,
                  mortgageInterestOnly,
                  interestOnlyMonthly,
                  cashPurchase,
                  shouldCalculateCMHC,
                  cmhcPremium,
                  purchaseAdditionalFinancing,
                  propertyInCanada
                ) => {
                  if (cashPurchase) {
                    return 0;
                  }

                  let mortgageAmount = null;

                  if (isValue(purchasePrice, downPayment)) {
                    mortgageAmount = purchasePrice - downPayment;

                    if (
                      shouldCalculateCMHC &&
                      cmhcPremium &&
                      propertyInCanada !== 0
                    ) {
                      mortgageAmount += cmhcPremium;
                    }
                  }

                  const firstMortgageMonthlyPayment =
                    mortgageFormSectionCalculator(
                      mortgageAmount,
                      interestRate,
                      mortgageInterestOnly,
                      interestOnlyMonthly,
                      amortization
                    );

                  const otherFinancingMonthlyPayment = Array.isArray(
                    purchaseAdditionalFinancing
                  )
                    ? purchaseAdditionalFinancing.reduce((acc, curr) => {
                        return curr.monthlyPaymentTable
                          ? acc + convertUnitMoney(curr.monthlyPaymentTable)
                          : acc;
                      }, 0)
                    : 0;

                  return (
                    firstMortgageMonthlyPayment + otherFinancingMonthlyPayment
                  );
                },
              },
            ],
          },
        ],
      },
      {
        sectionLabel: 'Closing Costs',
        sectionDescription:
          'Add information about your closing costs. You can itemize or enter a total amount.',
        sectionValue: 'cashRequiredToClose',
        schema: [
          {
            type: FormUIOptions.YesNo,
            hideLabel: true,
            yesLabel: 'Total',
            noLabel: 'Itemize',
            toggleColour: 'green',
            name: 'enterTotalClosingCosts',
            description: 'Enter closing costs as a single amount or itemize',
            defaultValue: enterTotalDefault,
            layout: 'default',
          },
          {
            type: FormUIOptions.Money,
            name: 'closingCostNotItemized',
            label: 'Closing Costs',
            description: 'Enter the total estimated amount of closing costs.',
            condition: {
              field: 'enterTotalClosingCosts',
              isLocal: true,
              value: true,
            },
          },
          {
            type: FormUIOptions.TableInline,
            layout: 'default',
            itemName: 'Closing Cost',
            label: 'Closing Costs',
            name: 'closingCosts',
            defaultItems: [
              {
                closingCostName: 'Building Inspection',
                closingCostAmount: purchaseDefaults.buildingInspection,
              },
              {
                closingCostName: 'Appraisal',
                closingCostAmount: purchaseDefaults.appraisal,
              },
              {
                closingCostName: 'Legal Fees',
                closingCostAmount: purchaseDefaults.legalFees,
              },
              {
                closingCostName: 'Reserve Fund',
                closingCostAmount: '',
              },
              {
                closingCostName: 'Environment Assessment',
                closingCostAmount: '',
              },
              {
                closingCostName: 'Land Transfer Tax',
                closingCostAmount: '',
              },
            ],
            tableSchema: [
              {
                type: FormUIOptions.Text,
                label: 'Name',
                layout: 'default',
                name: 'closingCostName',
              },
              {
                type: FormUIOptions.Money,
                label: 'Amount',
                layout: 'default',
                name: 'closingCostAmount',
              },
            ],
            condition: {
              field: 'enterTotalClosingCosts',
              isLocal: true,
              value: false,
            },
          },
          {
            type: FormUIOptions.CalculatedSection,
            groupStart: true,
            name: 'cashRequiredToCloseCalculations',
            schema: [
              {
                type: FormUIOptions.Calculated,
                label: 'Total',
                layout: 'default',
                name: 'totalCashRequiredToClose',
                unit: '$',
                derivedFrom: [
                  'closingCosts',
                  'closingCostNotItemized',
                  'enterTotalClosingCosts',
                ],
                calcFunction: (
                  closingCosts,
                  closingCostNotItemized,
                  enterTotalClosingCosts
                ) => {
                  if (enterTotalClosingCosts) {
                    return getNumberFromString(closingCostNotItemized);
                  }
                  return getTableSum(closingCosts, 'closingCostAmount');
                },
              },
            ],
          },
        ],
      },
      {
        sectionLabel: 'Rental Income',
        sectionValue: 'unitDetails',
        sectionDescription: 'Add rent information about rental units.',
        sectionAfterDescription:
          'Indicate which units will be rehabbed and provide the expected rent after rehab. Add rehab details for each unit by clicking the edit button.',
        isBeforeAfter: true,
        sectionHeadersType: 'tableModal',
        tableModalUnless: 'enterTotalRentalIncome',
        schema: [
          {
            type: FormUIOptions.YesNo,
            name: 'enterTotalRentalIncome',
            hideLabel: true,
            yesLabel: 'Total',
            noLabel: 'Units',
            toggleColour: 'green',
            description:
              'Enter rental income as a single amount or itemize. Entering as a single amount will disable the rent roll and some calculation results.',
            defaultValue: enterTotalDefault,
            //trying to specify it will be absolute positioned but only for the after (rehab) section
            afterPosition: 'absolute',
            layout: 'default',
          },
          {
            type: FormUIOptions.UnitMoney,
            name: 'rentalIncomeNotItemized',
            label: 'Rental Income',
            description:
              'Enter the total estimated amount of rent per month or year.',
            selectOptions: FormUIOptions.selectOptions.monthly,
            condition: {
              field: 'enterTotalRentalIncome',
              isLocal: true,
              value: true,
            },
          },
          {
            type: FormUIOptions.TableModal,
            layout: 'default',
            itemName: 'Unit',
            name: 'grossIncomePerUnit',
            label: 'Rental Units',
            modalDescription:
              'Add information about your rental units. Only the rent amount is required, but you can add additional information to create a rent roll',
            condition: {
              field: 'enterTotalRentalIncome',
              isLocal: true,
              value: [false, undefined, ''],
            },
            tableSchema: [
              {
                type: FormUIOptions.Text,
                label: 'Unit Name',
                layout: 'default',
                name: 'unitName',
                before: true,
                after: true,
              },
              {
                type: FormUIOptions.UnitMoney,
                label: 'Rent',
                layout: 'default',
                name: 'rent',
                selectOptions: FormUIOptions.selectOptions.monthly,
                required: true,
                before: true,
                after: true,
              },
              {
                type: FormUIOptions.YesNo,
                layout: 'tableNoLabel',
                label: 'Rehab?',
                yesLabel: 'Rehab',
                noLabel: 'No',
                name: 'unitRehab',
                defaultValue: false,
                before: false,
                after: true,
              },
              {
                type: FormUIOptions.Money,
                label: 'Unit Rehab Cost',
                description: 'Cost to rehab the unit',
                name: 'unitRehabCost',
                before: false,
                after: true,
                condition: {
                  field: 'unitRehab',
                  isLocal: true,
                  value: true,
                },
              },
              {
                type: FormUIOptions.Money,
                label: 'Tenant Buyout',
                description: 'Cost to buyout the tenant',
                name: 'unitBuyoutCost',
                before: false,
                after: true,
                condition: {
                  field: 'unitRehab',
                  isLocal: true,
                  value: true,
                },
              },
              {
                type: FormUIOptions.UnitTime,
                label: 'Unit Rehab Length',
                description: 'Length of time the unit will be vacant for rehab',
                name: 'unitRehabVacancy',
                selectOptions: FormUIOptions.selectOptions.weekMonth,
                before: false,
                after: true,
                condition: {
                  field: 'unitRehab',
                  isLocal: true,
                  value: true,
                },
              },
              {
                type: FormUIOptions.tableSection,
                label: 'Unit Details',
                name: 'unitDetails',
                layout: 'default',
                name: 'unitDetails',
                before: true,
                after: false,
                schema: [
                  {
                    type: FormUIOptions.Select,
                    label: 'Bedrooms',
                    layout: 'default',
                    name: 'numberOfBedrooms',
                    options: numberOptions,
                  },
                  {
                    type: FormUIOptions.Select,
                    label: 'Bathrooms',
                    layout: 'default',
                    name: 'numberOfBathrooms',
                    options: numberOptions,
                  },
                  {
                    type: FormUIOptions.Number,
                    label: 'Square Feet',
                    layout: 'default',
                    name: 'squareFootage',
                    unit: 'sq ft',
                  },
                  {
                    type: FormUIOptions.UnitMoney,
                    label: 'Yearly Rent Increase',
                    layout: 'default',
                    name: 'yearlyRentIncrease',
                    selectOptions: FormUIOptions.selectOptions.percentage,
                  },
                  {
                    type: FormUIOptions.TableModal,
                    label: 'Unit Features',
                    layout: 'default',
                    name: 'unitFeatures',
                    itemName: 'Feature',
                    before: true,
                    tableSchema: [
                      {
                        type: FormUIOptions.Text,
                        label: 'Feature',
                        layout: 'default',
                        name: 'feature',
                        before: true,
                      },
                    ],
                  },
                ],
              },
              {
                type: FormUIOptions.tableSection,
                label: 'Tenant Details',
                layout: 'default',
                name: 'tenantDetails',
                before: true,
                after: false,
                schema: [
                  {
                    type: FormUIOptions.YesNo,
                    label: 'Is the unit occupied?',
                    layout: 'default',
                    name: 'isOccupied',
                    defaultValue: true,
                  },
                  {
                    type: FormUIOptions.Select,
                    label: 'Occupants',
                    layout: 'default',
                    name: 'numberOfOccupants',
                    options: numberOptions,
                  },
                  {
                    type: FormUIOptions.TableModal,
                    label: 'Tenant Names',
                    layout: 'default',
                    name: 'tenantNames',
                    itemName: 'Name',
                    before: true,
                    tableSchema: [
                      {
                        type: FormUIOptions.Text,
                        label: 'Name',
                        layout: 'default',
                        name: 'name',
                        before: true,
                      },
                    ],
                  },
                  {
                    type: FormUIOptions.Date,
                    label: 'Lease Start',
                    layout: 'default',
                    name: 'leaseStart',
                  },
                  {
                    type: FormUIOptions.Select,
                    label: 'Lease Term',
                    layout: 'default',
                    name: 'leaseTerm',
                    options: [
                      {
                        label: 'Month-to-month',
                        layout: 'default',
                        value: 'Month-to-month',
                      },
                      {
                        label: '1 Year',
                        layout: 'default',
                        value: '1 Year',
                      },
                      {
                        label: '2 Years',
                        layout: 'default',
                        value: '2 years',
                      },
                      {
                        label: 'At will',
                        layout: 'default',
                        value: 'At will',
                      },
                      {
                        label: 'Other',
                        layout: 'default',
                        value: 'other',
                      },
                    ],
                  },
                  {
                    type: FormUIOptions.Text,
                    label: 'Other Lease Term',
                    layout: 'default',
                    name: 'leaseTermOther',
                    condition: {
                      field: 'leaseTerm',
                      isLocal: true,
                      value: 'other',
                    },
                  },
                  {
                    type: FormUIOptions.Select,
                    label: 'Rent Due Date',
                    layout: 'default',
                    name: 'rentDueDate',
                    options: rentDueDateOptions,
                  },
                  {
                    type: FormUIOptions.Text,
                    label: 'Other Due Date',
                    layout: 'default',
                    name: 'rentDueDateOther',
                    condition: {
                      field: 'rentDueDate',
                      isLocal: true,
                      value: 'other',
                    },
                  },
                  {
                    type: FormUIOptions.YesNo,
                    label: 'Past due rent?',
                    layout: 'default',
                    name: 'pastDueRent',
                  },
                  {
                    type: FormUIOptions.Money,
                    label: 'Amount Past Due',
                    layout: 'default',
                    name: 'amountPastDue',
                    unit: '$',
                    condition: {
                      field: 'pastDueRent',
                      isLocal: true,
                      value: true,
                    },
                  },
                  {
                    type: FormUIOptions.Money,
                    label: 'Deposit',
                    layout: 'default',
                    name: 'securityDeposit',
                  },
                ],
              },
              {
                type: FormUIOptions.TextArea,
                label: 'Notes',
                layout: 'default',
                name: 'notes',
                before: true,
                after: false,
              },
            ],
          },

          {
            type: FormUIOptions.UnitMoney,
            label: 'Vacancy Rate',
            layout: 'default',
            showAltValue: true,
            description:
              'Income total displayed will be adjusted for vacancy rate. Enter amount or percentage of the Gross Rent',
            name: 'vacancyDeduction',
            unit: '%',
            derivedFrom: 'rentalIncomePreVacancy',
            selectOptions: FormUIOptions.selectOptions.percentage,
          },
          {
            type: FormUIOptions.CalculatedSection,
            name: 'rentalIncomeCalculations',
            groupStart: true,
            schema: [
              {
                type: FormUIOptions.Calculated,
                label: 'Total (Pre-Vacancy)',
                layout: 'default',
                name: 'rentalIncomePreVacancy',
                per: 'month',
                deEmphasize: true,
                unit: '$',
                derivedFrom: [
                  'grossIncomePerUnit',
                  'enterTotalRentalIncome',
                  'rentalIncomeNotItemized',
                ],
                calcFunction: (
                  grossIncomePerUnit,
                  enterTotalRentalIncome,
                  rentalIncomeNotItemized
                ) => {
                  if (!enterTotalRentalIncome) {
                    if (Array.isArray(grossIncomePerUnit)) {
                      if (
                        grossIncomePerUnit.find(
                          (unit) => unit.beforeRent || isValue(unit.unitRehab)
                        )
                      ) {
                        const rentalIncome = grossIncomePerUnit.reduce(
                          (acc, curr) => {
                            if (curr.unitRehab) {
                              return acc + convertUnitMoney(curr.rent || 0);
                            } else {
                              return (
                                acc + convertUnitMoney(curr.beforeRent || 0)
                              );
                            }
                          },
                          0
                        );
                        return Math.round(rentalIncome * 100) / 100;
                      }

                      return (
                        Math.round(
                          getTotalRentIncome(grossIncomePerUnit) * 100
                        ) / 100 || 0
                      );
                    } else {
                      return 0;
                    }
                  } else {
                    return getNumberFromString(rentalIncomeNotItemized);
                  }
                },
              },
              {
                type: FormUIOptions.Calculated,
                label: 'Total (Post-Vacancy)',
                per: 'month',
                layout: 'default',
                name: 'totalGrossRentalIncome',
                unit: '$',
                derivedFrom: ['rentalIncomePreVacancy', 'vacancyDeduction'],
                calcFunction: (rentalIncomePreVacancy, vacancyDeduction) => {
                  const vacancy = convertUnitMoney(vacancyDeduction);

                  const vacancyAdjusted =
                    rentalIncomePreVacancy && isValue(vacancy)
                      ? Math.round((rentalIncomePreVacancy - vacancy) * 100) /
                        100
                      : rentalIncomePreVacancy;

                  return vacancyAdjusted;
                },
              },
            ],
          },
        ],
      },
      {
        sectionLabel: 'Optional: Other Income',
        sectionDescription: 'Add any additional income sources',
        sectionAfterDescription:
          'Add additional income sources and modify the value of existing income sources.',
        sectionValue: 'nonRentIncome',
        sectionCollapsed: false,
        isBeforeAfter: true,
        schema: [
          {
            type: FormUIOptions.YesNo,
            name: 'enterTotalOtherIncome',
            hideLabel: true,
            yesLabel: 'Total',
            noLabel: 'Itemize',
            toggleColour: 'green',
            description: 'Enter other income as a single amount or itemize',
            afterPosition: 'absolute',
            defaultValue: enterTotalDefault,
            layout: 'default',
          },
          {
            type: FormUIOptions.UnitMoney,
            name: 'otherIncomeNotItemized',
            label: 'Other Income',
            description: 'Enter the total estimated amount of other income.',
            selectOptions: FormUIOptions.selectOptions.monthly,
            condition: {
              field: 'enterTotalOtherIncome',
              isLocal: true,
              value: true,
            },
          },
          {
            type: FormUIOptions.TableInline,
            layout: 'default',
            itemName: 'Income',
            label: 'Other Income',
            name: 'otherPropertyIncome',
            defaultItems: [
              {
                otherIncomeName: 'Parking',
                otherIncomeAmount: '',
              },
              {
                otherIncomeName: 'Laundry',
                otherIncomeAmount: '',
              },
              {
                otherIncomeName: 'Storage',
                otherIncomeAmount: '',
              },
            ],
            tableSchema: [
              {
                type: FormUIOptions.Text,
                label: 'Name',
                layout: 'default',
                name: 'otherIncomeName',
              },
              {
                type: FormUIOptions.UnitMoney,
                selectOptions: FormUIOptions.selectOptions.monthly,
                label: 'Amount',
                layout: 'default',
                name: 'otherIncomeAmount',
              },
            ],
            condition: {
              field: 'enterTotalOtherIncome',
              isLocal: true,
              value: false,
            },
          },
          {
            type: FormUIOptions.CalculatedSection,
            groupStart: true,
            name: 'totalOtherIncomeSection',
            schema: [
              {
                type: FormUIOptions.Calculated,
                label: 'TOTAL',
                layout: 'default',
                per: 'month',
                name: 'totalOtherIncome',
                unit: '$',
                derivedFrom: [
                  'otherPropertyIncome',
                  'otherIncomeNotItemized',
                  'enterTotalOtherIncome',
                ],
                calcFunction: (
                  otherPropertyIncome,
                  otherIncomeNotItemized,
                  enterTotalOtherIncome
                ) => {
                  if (enterTotalOtherIncome) {
                    return convertUnitMoney(otherIncomeNotItemized) || 0;
                  }
                  return getTableSum(otherPropertyIncome, 'otherIncomeAmount');
                },
              },
            ],
          },
        ],
      },
      {
        sectionLabel: 'Operating Expenses',
        sectionAfterLabel: 'New Operating Expenses',
        sectionValue: 'operatingExpenses',
        sectionDescription:
          'Percentage values are based on gross rental income (before vacancy / month).',
        isBeforeAfter: true,
        schema: [
          {
            type: FormUIOptions.YesNo,
            name: 'enterTotalExpenses',
            hideLabel: true,
            yesLabel: 'Total',
            noLabel: 'Itemize',
            toggleColour: 'green',
            description:
              'Enter operating expenses as a single amount / percentage or itemize',
            defaultValue: enterTotalDefault,
            afterPosition: 'absolute',
            layout: 'default',
          },
          {
            type: FormUIOptions.UnitMoney,
            name: 'expensesNotItemized',
            label: 'Operating Expenses',
            description:
              'Enter the total estimated amount of operating expenses. Percentage amount is based on gross rental income (before vacancy / month).',
            derivedFrom: 'rentalIncomePreVacancy',
            selectOptions: FormUIOptions.selectOptions.monthYearPercent,
            showAltValue: true,
            condition: {
              field: 'enterTotalExpenses',
              isLocal: true,
              value: true,
            },
          },
          {
            type: FormUIOptions.TableInline,
            label: 'Operating Expenses',
            layout: 'default',
            description: '',
            name: 'operatingExpenses',
            itemName: 'Expense',
            defaultItems: [
              {
                expenseName: 'Property Tax',
                expenseAmount: '',
              },
              {
                expenseName: 'Insurance',
                expenseAmount: '',
              },
              {
                expenseName: 'Repairs & Maintenance',
                expenseAmount: {
                  value: 5,
                  unit: '%',
                  derivedFrom: 'rentalIncomePreVacancy',
                },
              },
              {
                expenseName: 'Property Management',
                expenseAmount: {
                  value: 7,
                  unit: '%',
                  derivedFrom: 'rentalIncomePreVacancy',
                },
              },
              {
                expenseName: 'Heat',
                expenseAmount: '',
              },
              {
                expenseName: 'Hydro',
                expenseAmount: '',
              },
              {
                expenseName: 'Water',
                expenseAmount: '',
              },
              {
                expenseName: 'Garbage',
                expenseAmount: '',
              },
              {
                expenseName: 'Water Heater Rental',
                expenseAmount: '',
              },
              {
                expenseName: 'Tenant Gifts',
                expenseAmount: '',
              },
            ],
            tableSchema: [
              {
                type: FormUIOptions.Text,
                label: 'Name',
                layout: 'default',
                name: 'expenseName',
              },
              {
                type: FormUIOptions.UnitMoney,
                label: 'Value',
                layout: 'default',
                name: 'expenseAmount',
                showAltValue: true,
                derivedFrom: 'rentalIncomePreVacancy',
                selectOptions: FormUIOptions.selectOptions.monthYearPercent,
              },
            ],
            condition: {
              field: 'enterTotalExpenses',
              isLocal: true,
              value: false,
            },
          },
          {
            type: FormUIOptions.CalculatedSection,
            groupStart: true,
            name: 'totalOperatingExpensesCalculations',
            schema: [
              {
                type: FormUIOptions.Calculated,
                label: 'Total Expenses',
                layout: 'default',
                name: 'totalOperatingExpenses',
                per: 'month',
                unit: '$',
                derivedFrom: [
                  'operatingExpenses',
                  'rentalIncomePreVacancy',
                  'expensesNotItemized',
                  'enterTotalExpenses',
                ],
                calcFunction: (
                  operatingExpenses,
                  rentalIncomePreVacancy,
                  expensesNotItemized,
                  enterTotalExpenses
                ) => {
                  let total = null;

                  if (enterTotalExpenses) {
                    total = convertUnitMoney(expensesNotItemized, {
                      rentalIncomePreVacancy,
                    });
                  } else {
                    total = getTableSum(operatingExpenses, 'expenseAmount', {
                      rentalIncomePreVacancy,
                    });
                  }

                  return total ? Math.round(total * 100) / 100 : 0;
                },
              },
              {
                type: FormUIOptions.Calculated,
                label: 'Cash Flow',
                tooltipHeading: 'Cash Flow',
                groupStart: true,
                description: 'Monthly cash flow',
                tooltipFormula: [
                  'Cash Flow',
                  '=',
                  'Total Gross Income',
                  '-',
                  'Total Operating Expenses',
                  '-',
                  'Total Monthly Mortgage Payment',
                ],
                name: 'monthlyPreTaxCashFlow',
                layout: 'default',
                emphasize: true,
                unit: '$',
                per: 'month',
                derivedFrom: [
                  'totalOperatingExpenses',
                  'totalMonthlyMortgagePayment',
                  'cashPurchase',
                  'totalGrossRentalIncome',
                  'totalOtherIncome',
                ],
                calcFunction: (
                  totalOperatingExpenses,
                  totalMonthlyMortgagePayment,
                  cashPurchase,
                  ...args
                ) => {
                  const monthlyGOI = getSum(...args);

                  if (
                    isValue(
                      monthlyGOI,
                      totalOperatingExpenses,
                      totalMonthlyMortgagePayment
                    )
                  ) {
                    const monthlyPreTaxIncome =
                      monthlyGOI - totalOperatingExpenses;

                    if (cashPurchase) {
                      return monthlyPreTaxIncome;
                    }

                    const monthlyPreTaxCashFlow =
                      monthlyPreTaxIncome - totalMonthlyMortgagePayment;

                    const result = monthlyPreTaxCashFlow;
                    return result;
                  } else {
                    return null;
                  }
                },
              },
              {
                type: FormUIOptions.Calculated,
                label: 'Debt Coverage Ratio',
                name: 'debtCoverageRatio',
                tooltipHeading: 'Debt Coverage Ratio (DCR, DSCR)',
                decimalPlaces: 2,
                description:
                  'The ratio between the net operating income and the monthly debt service.',
                tooltipFormula: [
                  'Debt Coverage Ratio',
                  '=',
                  'Net Operating Income',
                  '/',
                  'Debt Service',
                ],
                condition: {
                  field: 'cashPurchase',
                  value: false,
                },
                layout: 'default',
                decimals: 2,
                emphasize: true,
                red: 1,
                green: 1.1,
                derivedFrom: [
                  'totalOperatingExpenses',
                  'totalMonthlyMortgagePayment',
                  'cashPurchase',
                  'totalRehabFinancingMonthlyPayment',
                  'totalGrossRentalIncome',
                  'totalOtherIncome',
                ],

                calcFunction: (
                  totalOperatingExpenses,
                  totalMonthlyMortgagePayment,
                  cashPurchase,
                  totalRehabFinancingMonthlyPayment,
                  ...args
                ) => {
                  if (cashPurchase) {
                    return null;
                  }

                  if (!totalRehabFinancingMonthlyPayment) {
                    totalRehabFinancingMonthlyPayment = 0;
                  }

                  if (
                    !isValue(
                      totalMonthlyMortgagePayment,
                      totalOperatingExpenses
                    )
                  ) {
                    return null;
                  }

                  const monthlyNOI =
                    getSum(...args) - (totalOperatingExpenses || 0);

                  const monthlyPayment =
                    totalMonthlyMortgagePayment +
                    totalRehabFinancingMonthlyPayment;
                  const res = monthlyNOI / monthlyPayment;

                  return res;
                },
              },
            ],
          },
        ],
      },
      {
        sectionLabel: 'Advanced: Purchase Valuation',
        sectionDescription:
          'Optional feature to use a purchase valuation distinct from the purchase price.',
        sectionCollapsed: true,
        excludeFromEmbeddedForm: true,
        sectionValue: 'monthlyCashFlow',
        schema: [
          {
            type: FormUIOptions.YesNo,
            label: 'Use Purchase Valuation?',
            name: 'usePurchaseValuation',
            defaultValue: false,
            layout: 'default',
          },
          {
            type: FormUIOptions.Radio,
            label: 'Valuation Method',
            required: true,
            groupStart: true,
            description:
              'Choose an option to calculate the property value & target cap rate on purchase. If you choose the income approach, the cap rate will be used to estimate the property value. If you choose the direct comparison approach, you will need to manually enter the estimated market value.',
            layout: 'default',
            name: 'targetValueSelectPurchase',
            defaultValue: 1,
            options: [
              {
                label: 'Income Approach (Enter Cap Rate)',
                layout: 'default',
                value: 1,
              },
              {
                label: 'Direct Comparison (Enter Value)',
                layout: 'default',
                value: 2,
              },
            ],
            condition: {
              field: 'usePurchaseValuation',
              isLocal: true,
              value: true,
            },
          },
          {
            type: FormUIOptions.Number,
            unit: '%',
            label: 'Target Cap Rate',
            required: true,
            defaultValue: longTermDefaults.capRate,
            description:
              'Cap rate for similar properties in the area, or desired cap rate on purchase. Used to analyze the purchase price of the property and to calculate the property value in the future.',
            name: 'localCapRateManual',
            layout: 'default',
            condition: [
              {
                field: 'targetValueSelectPurchase',
                isLocal: true,
                value: 1,
              },
              {
                field: 'usePurchaseValuation',
                isLocal: true,
                value: true,
              },
            ],
          },
          {
            type: FormUIOptions.Money,
            label: 'Target Market Price',
            required: true,
            description:
              'Desired market price of the property. Used to calculate the target cap rate on purchase and estimate the property value.',
            name: 'marketPurchasePriceManual',
            condition: [
              {
                field: 'targetValueSelectPurchase',
                isLocal: true,
                value: 2,
              },
              {
                field: 'usePurchaseValuation',
                isLocal: true,
                value: true,
              },
            ],
          },
          {
            type: FormUIOptions.CalculatedSection,
            name: 'purchaseValuationCalculations',
            condition: {
              field: 'usePurchaseValuation',
              isLocal: true,
              value: true,
            },
            groupStart: true,
            schema: [
              {
                type: FormUIOptions.Calculated,
                unit: '$',
                label: 'Property Value on Purchase (est)',
                tooltipHeading: 'Property Value on Purchase',
                groupStart: true,
                emphasize: true,
                large: true,
                name: 'marketPurchasePrice',
                description: 'Estimated market value of the property',
                tooltipFormula: [
                  'Property Value on Purchase',
                  '=',
                  'Net Operating Income',
                  '/',
                  'Target Cap Rate',
                ],
                derivedFrom: [
                  'localCapRate',
                  'totalOperatingExpenses',
                  'targetValueSelectPurchase',
                  'marketPurchasePriceManual',
                  'usePurchaseValuation',
                  'purchasePrice',
                  'totalGrossRentalIncome',
                  'totalOtherIncome',
                ],
                calcFunction: (
                  localCapRate,
                  totalOperatingExpenses,
                  targetValueSelectPurchase,
                  marketPurchasePriceManual,
                  usePurchaseValuation,
                  purchasePrice,
                  ...args
                ) => {
                  if (!usePurchaseValuation) {
                    return purchasePrice;
                  }

                  if (targetValueSelectPurchase === 2) {
                    return marketPurchasePriceManual;
                  }

                  const netOperatingIncome =
                    getSum(...args) * 12 - totalOperatingExpenses * 12;

                  const marketPurchasePrice =
                    netOperatingIncome / (localCapRate / 100);
                  return Math.round(marketPurchasePrice * 100) / 100;
                },
                condition: [
                  {
                    field: 'usePurchaseValuation',
                    isLocal: true,
                    value: true,
                  },
                ],
              },
              {
                type: FormUIOptions.Calculated,
                unit: '%',
                decimalPlaces: 2,
                label: 'Target Cap Rate on Purchase',
                name: 'localCapRate',
                tooltipHeading: 'Target Cap Rate on Purchase',
                description:
                  'Cap rate based on the market price of the property and the income and expenses you entered.',
                tooltipFormula: [
                  'Target Cap Rate on Purchase',
                  '=',
                  'Net Operating Income',
                  '/',
                  'Market Purchase Price',
                ],
                derivedFrom: [
                  'marketPurchasePriceManual',
                  'totalOperatingExpenses',
                  'targetValueSelectPurchase',
                  'localCapRateManual',
                  'totalGrossRentalIncome',
                  'totalOtherIncome',
                ],
                calcFunction: (
                  marketPurchasePrice,
                  totalOperatingExpenses,
                  targetValueSelectPurchase,
                  localCapRateManual,
                  ...args
                ) => {
                  if (targetValueSelectPurchase === 1) {
                    return localCapRateManual;
                  }

                  const netOperatingIncome =
                    getSum(...args) * 12 - totalOperatingExpenses * 12;

                  const capRate = netOperatingIncome / marketPurchasePrice;
                  return Math.round(capRate * 10000) / 100;
                },
                condition: [
                  {
                    field: 'usePurchaseValuation',
                    isLocal: true,
                    value: true,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        sectionLabel: 'Property Assumptions',
        sectionDescription:
          'Assumed metrics about the property. You can modify the default values.',
        sectionValue: 'appreciationDetails',
        sectionCollapsed: true,
        excludeFromEmbeddedForm: true,
        schema: [
          {
            type: FormUIOptions.Number,
            label: 'Passive Appreciation',
            layout: 'default',
            defaultValue: longTermDefaults.appreciation,
            description:
              'Estimated annual passive appreciation of the property, expressed as a percentage of the purchase price',
            name: 'marketAppreciationPercent',
            unit: '%',
          },
          {
            type: FormUIOptions.Number,
            unit: '%',
            label: 'Expenses Increase',
            defaultValue: longTermDefaults.expenses,
            layout: 'default',
            name: 'yearlyExpenseIncrease',
            description:
              'Estimated annual increase in expenses, expressed as a percentage of the total expenses',
          },
          {
            type: FormUIOptions.Number,
            unit: '%',
            label: 'Income Increase',
            defaultValue: longTermDefaults.income,
            name: 'yearlyIncomeIncrease',
            description:
              'Estimated annual increase in income, expressed as a percentage of the total income',
          },
          {
            type: FormUIOptions.Number,
            unit: '%',
            label: 'Sale Cost',
            defaultValue: longTermDefaults.saleCost,
            name: 'saleCostPercent',
            description:
              'Estimated cost of selling the property, expressed as a percentage of the sale price',
          },
        ],
      },
      {
        sectionValue: 'embedDataNeeded',
        onlyInEmbeddedForm: true,
        sectionLabel: 'Email Address',
        sectionDescription:
          'Your email is needed to associate the data with you once you sign up or log in.',
        schema: [
          {
            type: FormUIOptions.Email,
            description:
              'Your email is needed to associate the data with you when you sign up or log in. Please use the same email you would use to sign up or login.',
            label: 'Email',
            name: 'embedEmail',
            required: true,
            onlyInEmbeddedForm: true,
            placeholder: 'Enter your email',
          },
        ],
      },
    ],
  },

  calculations: calculations,
  simpleCalculation: simpleCalculation,

  keyMetrics: [
    {
      sectionValue: 'keyMetrics1',
      layout: 'column',
      schema: [
        {
          type: FormUIOptions.Money,
          label: 'Purchase Price',
          layout: 'default',
          name: 'purchasePrice',
          unit: '$',
        },
        {
          type: FormUIOptions.Money,
          label: 'Total Cash Invested',
          tooltipHeading: 'Total Cash Invested',
          description:
            'The total of the original downpayment and closing costs.',
          tooltipFormula: [
            'Total Cash Invested',
            '=',
            'Down Payment',
            '+',
            'Closing Costs',
          ],
          layout: 'default',
          name: 'totalCashInvestedOnPurchase',
          unit: '$',
        },
        {
          type: FormUIOptions.Number,
          label: 'Cap Rate',
          layout: 'default',
          tooltipHeading: 'Cap Rate',
          description:
            'The ratio of Net Operating Income (NOI) to the property asset value.',
          tooltipFormula: ['Cap Rate', '=', 'NOI', '/', 'Purchase Price'],
          name: 'capRateInitial',
          unit: '%',
        },
        {
          type: FormUIOptions.Money,
          label: 'Cash Flow',
          layout: 'default',
          name: 'annualPreTaxCashFlow',
          tooltipHeading: 'Cash Flow',

          description: 'The monthly cash flow of the property',
          tooltipFormula: [
            'Cash Flow',
            '=',
            'Monthly Income',
            '-',
            'Monthly Expenses',
          ],
          dynamic: true,
          isAnnual: true,
          showMonthlyAnnual: true,
          unit: '$',
        },
      ],
    },
  ],

  rentRollMetrics: [
    {
      sectionValue: 'rentRoll1',
      layout: 'row',
      schema: [
        {
          type: FormUIOptions.Number,
          label: 'Units',
          layout: 'default',
          decimals: 0,
          name: 'units',
        },
        {
          type: FormUIOptions.Text,
          label: 'Occupied',
          decimals: 0,
          layout: 'default',
          name: 'occupied',
        },

        {
          type: FormUIOptions.Money,
          label: 'Current Rent',
          layout: 'default',
          name: 'rentalIncomePreVacancy',
          unit: '$',
        },
        {
          type: FormUIOptions.Money,
          label: 'Market Rent',
          decimals: 0,
          layout: 'default',
          name: 'marketRent',
          unit: '$',
        },
        {
          type: FormUIOptions.Text,
          label: 'Lift',
          layout: 'default',
          name: 'lift',
          unit: '$',
          textColor: 'green-700',
        },
      ],
    },
  ],

  //schema for the investment metrics section, each calcFunction returns an object with the value and the message. Could be refactored as the string templating is not ideal
  //The values are 0 - for does not pass, 1 - for pass, 2 - for invalid input
  investmentMetrics: [
    {
      type: FormUIOptions.Money,
      label: 'Purchase Price',
      description: 'Maximum allowable purchase price.',
      name: 'purchasePriceCriteria',
      derivedFrom: ['purchasePrice', 'maxPurchasePrice'],
      calcFunction: (purchasePrice, maxPurchasePrice) => {
        const maxPrice = maxPurchasePrice || criteriaDefaults.maxPurchasePrice;

        if (isValue(purchasePrice, maxPrice)) {
          if (purchasePrice > maxPrice) {
            return {
              value: 0,
              message: `Purchase price of $${getNumberWithCommas(
                purchasePrice
              )} is higher than the maximum of $${getNumberWithCommas(
                maxPrice
              )}.`,
            };
          }
          if (purchasePrice < maxPrice) {
            return {
              value: 1,
              message: `Purchase price of $${getNumberWithCommas(
                purchasePrice
              )} is lower than the maximum of $${getNumberWithCommas(
                maxPrice
              )}.`,
            };
          }
        }
        return {
          value: 2,
          message:
            'Missing value for purchase price or maximum purchase price.',
        };
      },
      condition: {
        field: 'screenPurchasePrice',
        value: true,
      },
    },
    {
      type: FormUIOptions.Money,
      label: 'Cash Needed',
      description:
        'Maximum allowable cash needed (downpayment + closing costs).',
      name: 'cashNeededCriteria',
      derivedFrom: [
        'totalCashInvestedOnPurchase',
        'cashNeededPurchaseCriteria',
        'purchasePrice',
      ],
      calcFunction: (
        totalCashInvestedOnPurchase,
        maxCashNeededPurchase,
        purchasePrice
      ) => {
        const maxCashNeeded = convertUnitMoney(
          maxCashNeededPurchase || criteriaDefaults.cashNeededPurchaseCriteria,
          { purchasePrice }
        );

        if (isValue(totalCashInvestedOnPurchase, maxCashNeeded)) {
          if (totalCashInvestedOnPurchase >= maxCashNeeded) {
            return {
              value: 0,
              message: `Cash needed of $${getNumberWithCommas(
                totalCashInvestedOnPurchase
              )} is higher than the maximum of $${getNumberWithCommas(
                maxCashNeeded
              )}.`,
            };
          }
          if (totalCashInvestedOnPurchase <= maxCashNeeded) {
            return {
              value: 1,
              message: `Cash needed of $${getNumberWithCommas(
                Math.round(totalCashInvestedOnPurchase)
              )} is lower than the maximum of $${getNumberWithCommas(
                maxCashNeeded
              )}.`,
            };
          }
        }
        return {
          value: 2,
          message: 'Missing value for cash needed or maximum cash needed.',
        };
      },
      condition: {
        field: 'screenCashNeededPurchase',
        value: true,
      },
    },
    {
      type: FormUIOptions.Number,
      label: '1% Rule',
      name: 'onePercentRuleCriteria',
      description:
        'The monthly gross rent should be at least 1% of the purchase price.',
      derivedFrom: ['monthlyGOI', 'purchasePrice'],
      calcFunction: (monthlyGOI, purchasePrice) => {
        if (isValue(monthlyGOI, purchasePrice)) {
          const monthlyGOIValue = getNumberFromString(monthlyGOI);
          const purchasePriceValue = getNumberFromString(purchasePrice);

          const result = monthlyGOIValue / purchasePriceValue;
          if (result >= 0.01) {
            return {
              value: 1,
              message: `Passes 1% rule (${Math.round(result * 1000) / 10}%).`,
            };
          } else {
            return {
              value: 0,
              message: `Does not pass 1% rule (${
                Math.round(result * 1000) / 10
              }%).`,
            };
          }
        }
        return {
          value: 2,
          message: 'Missing value for monthly GOI or purchase price.',
        };
      },
      condition: {
        field: 'screenOnePercentRulePurchase',
        value: true,
      },
    },
    {
      type: FormUIOptions.Number,
      label: '50% Rule',
      name: 'fiftyPercentRuleCriteria',
      description:
        'The monthly expenses should be less than or equal to 50% of the monthly gross rent.',
      derivedFrom: ['totalMonthlyOperatingExpensesPurchase', 'monthlyGOI'],
      calcFunction: (totalMonthlyOperatingExpensesPurchase, monthlyGOI) => {
        if (isValue(totalMonthlyOperatingExpensesPurchase, monthlyGOI)) {
          const totalMonthlyOperatingExpensesPurchaseValue =
            getNumberFromString(totalMonthlyOperatingExpensesPurchase);
          const monthlyGOIValue = getNumberFromString(monthlyGOI);

          const result =
            totalMonthlyOperatingExpensesPurchaseValue / monthlyGOIValue;
          if (result <= 0.5) {
            return {
              value: 1,
              message: `Passes 50% rule (${Math.round(result * 1000) / 10}%).`,
            };
          } else {
            return {
              value: 0,
              message: `Does not pass 50% rule (${
                Math.round(result * 1000) / 10
              }%).`,
            };
          }
        }
        return {
          value: 2,
          message: 'Missing value for monthly expenses or monthly GOI.',
        };
      },
      condition: {
        field: 'screenFiftyPercentRulePurchase',
        value: true,
      },
    },
    {
      type: FormUIOptions.Number,
      label: 'Cash Flow',
      name: 'cashFlowCriteria',
      description:
        'Whether the property passes the minimum cash flow requirement.',
      derivedFrom: ['monthlyPreTaxCashFlow', 'minCashFlowPurchase'],
      calcFunction: (monthlyPreTaxCashFlow, minCashFlowPurchase) => {
        const minCashFlow =
          minCashFlowPurchase || criteriaDefaults.minCashFlowPurchase;

        if (isValue(monthlyPreTaxCashFlow, minCashFlow)) {
          if (monthlyPreTaxCashFlow >= minCashFlow) {
            return {
              value: 1,
              message: `Passes cash flow rule ($${getNumberWithCommas(
                Math.round(monthlyPreTaxCashFlow)
              )}).`,
            };
          } else {
            return {
              value: 0,
              message: `Does not pass cash flow rule ($${getNumberWithCommas(
                Math.round(monthlyPreTaxCashFlow)
              )}).`,
            };
          }
        }
        return {
          value: 2,
          message: 'Missing value for monthly cash flow.',
        };
      },
      condition: {
        field: 'screenCashFlowPurchase',
        value: true,
      },
    },
    {
      type: FormUIOptions.Number,
      label: 'Cap Rate (Purchase)',
      name: 'capRateCriteria',
      description: 'Whether the property passes the cap rate requirement.',
      derivedFrom: ['capRateInitial', 'minCapRatePurchase'],
      calcFunction: (capRateInitial, minCapRatePurchase) => {
        const minCapRate =
          minCapRatePurchase || criteriaDefaults.minCapRatePurchase;

        if (isValue(capRateInitial, minCapRate)) {
          if (capRateInitial >= minCapRate) {
            return {
              value: 1,
              message: `Passes cap rate rule (${capRateInitial}%)`,
            };
          } else {
            return {
              value: 0,
              message: `Does not pass cap rate rule (${capRateInitial}%)`,
            };
          }
        }
        return {
          value: 2,
          message: 'Missing value for cap rate or minimum cap rate',
        };
      },
      condition: {
        field: 'screenCapRatePurchase',
        value: true,
      },
    },
    {
      type: FormUIOptions.Number,
      label: 'Total ROI Year 1',
      name: 'totalRoiYear1Criteria',
      description:
        'Whether the property passes the total return on investment requirement.',
      derivedFrom: ['totalRoiYear1', 'minTotalRoiYear1Purchase'],
      calcFunction: (totalRoiYear1, minTotalRoiYear1Purchase) => {
        const minTotalRoiYear1 =
          minTotalRoiYear1Purchase || criteriaDefaults.minTotalRoiYear1Purchase;

        if (isValue(totalRoiYear1, minTotalRoiYear1)) {
          if (totalRoiYear1 >= minTotalRoiYear1) {
            return {
              value: 1,
              message: `Passes total ROI year 1 rule (${totalRoiYear1}%)`,
            };
          } else {
            return {
              value: 0,
              message: `Does not pass total ROI year 1 rule (${totalRoiYear1}%)`,
            };
          }
        }
        return {
          value: 2,
          message:
            'Missing value for total ROI year 1 or minimum total ROI year 1',
        };
      },
      condition: {
        field: 'screenTotalRoiYear1Purchase',
        value: true,
      },
    },
  ],
};
